import { get } from 'lodash';
import { axiosBase, axiosBaseForExternalApp } from '@/services/axios-base';
import { HRM_LINK, MANAGER_STATUS, PROJECT_TYPES } from '@/constants';
import { formatDateToVTextField } from '@/helpers';
import {
  ProjectDTO,
  ProjectRequestDTO,
  ProjectAssessmentDTO,
  ProjectAssessmentPutDTO,
  MemberPeerEvaluationDTO,
  ProjectPeerEvaluationRequestDTO,
  SelectionOptions,
  UserEngagementDTO,
  ProjectAssessmentAverageDropsDTO,
  HRMActivityDTO,
  HRMProjectDTO,
  UserEngagementAndActivityKeyMapRequestDTO,
  ProjectManager
} from '@/models';

export const fetchProjectsOfCurrentUser = (): Promise<{ data: ProjectDTO[] }> => {
  return axiosBase.get('/api/projects/currentLogin');
};

export const fetchProjectById = (id: string): Promise<{ data: ProjectDTO }> => {
  return axiosBase.get(`/api/project/${id}`);
};

export const createProject = (body: ProjectRequestDTO): Promise<{ data: ProjectDTO }> => {
  return axiosBase.post('/api/project', body);
};

export const updateProject = (body: ProjectRequestDTO): Promise<{ data: ProjectDTO }> => {
  return axiosBase.put('/api/project', body);
};

export const deleteProject = (id: number): Promise<void> => {
  return axiosBase.delete(`/api/project/${id}`);
};

export const fetchProjectEvaluation = (): Promise<{ data: ProjectDTO[] }> => {
  return axiosBase.get('/api/projects/evaluation');
};

export const putProjectEvaluation = (body: number[]): Promise<{ data: number[] }> => {
  return axiosBase.put('/api/projects/evaluation', body);
};

export const stopEvaluationProject = (projectId: number): Promise<void> => {
  return axiosBase.put(`/api/project/${projectId}/stopEvaluation`);
};

export const projectTypeMapping = {
  [PROJECT_TYPES.HOURLY_BASED]: 'Hourly-based',
  [PROJECT_TYPES.FIXED]: 'Fixed',
  [PROJECT_TYPES.INDIVIDUAL_TRACKING]: 'Individual Tracking'
};

export const buildProjectTypeOptions = (): SelectionOptions => [
  {
    text: projectTypeMapping[PROJECT_TYPES.INDIVIDUAL_TRACKING],
    value: PROJECT_TYPES.INDIVIDUAL_TRACKING
  }
];

export const filterProjectsMultiFieldsByKeywords = (
  projects: ProjectDTO[],
  keywords: string[],
  fields: string[]
): ProjectDTO[] => {
  if (keywords.length) {
    const trimmedKeywords = keywords.map(key => key.toString().trim().toLowerCase());
    return projects.filter((project: ProjectDTO) => {
      return trimmedKeywords.every((keyword: any) => {
        let filterWord = keyword;
        if (!isNaN(keyword) && keyword !== '') {
          filterWord = parseInt(keyword);
        }
        return fields.some(field => get(project, field, '').toString().trim().toLowerCase().includes(filterWord));
      });
    });
  }
  return projects;
};

export const saveUserEngagements = (
  body: UserEngagementAndActivityKeyMapRequestDTO,
  projectAssessmentId: number,
  confirm: boolean,
  key?: number
): Promise<{ data: UserEngagementDTO[] }> => {
  if (key !== undefined) {
    return axiosBase.post(`/api/assessment/${projectAssessmentId}/userEngagements?key=${key}&confirm=${confirm}`, body);
  }
  return axiosBase.post(`/api/assessment/${projectAssessmentId}/userEngagements?confirm=${confirm}`, body);
};

export const getUserEngagementByProjectAssessmentId = (
  projectAssessmentId: number
): Promise<{ data: UserEngagementDTO[] }> => {
  return axiosBase.get(`/api/assessment/${projectAssessmentId}/userEngagements`);
};

export const pagingProjectCard = (projects: ProjectDTO[], page: number, itemsPerPage: number): ProjectDTO[] => {
  if (itemsPerPage === -1) {
    return projects;
  }
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return projects.slice(startIndex, endIndex);
};

export const putProjectAssessment = (
  projectAssessmentData: ProjectAssessmentPutDTO
): Promise<{ data: ProjectAssessmentDTO }> => {
  return axiosBase.put(`/api/project/assessment?confirm=${projectAssessmentData.confirm}`, {
    id: projectAssessmentData.id,
    projectId: projectAssessmentData.projectId,
    ...projectAssessmentData.projectAssessmentData
  });
};

export const updatePeerRatingPoint = (
  projectPeerEvaluation: ProjectPeerEvaluationRequestDTO
): Promise<{ data: MemberPeerEvaluationDTO }> => {
  return axiosBase.put(`/api/peerEvaluation`, projectPeerEvaluation);
};

export const fetchProjectPeerEvaluations = (
  projectAssessmentId: number
): Promise<{ data: MemberPeerEvaluationDTO[] }> => {
  return axiosBase.get(`/api/assessment/${projectAssessmentId}/peerEvaluations`);
};

export const fetchMemberPeerEvaluations = (
  projectAssessmentId: number,
  userId: number
): Promise<{ data: MemberPeerEvaluationDTO[] }> => {
  return axiosBase.get(`/api/peerEvaluations?projectAssessmentId=${projectAssessmentId}&userId=${userId}`);
};

export const confirmMemberPeerEvaluations = (
  projectAssessmentId: number,
  userId: number,
  givingDrops: number,
  body: MemberPeerEvaluationDTO[]
): Promise<{ data: { userEngagement: UserEngagementDTO; peerEvaluations: MemberPeerEvaluationDTO[] } }> => {
  return axiosBase.put(
    `/api/peerEvaluation/confirm?projectAssessmentId=${projectAssessmentId}&userId=${userId}&givingDrops=${givingDrops}`,
    body
  );
};

export const fetchAverageSatAndComplexity = (): Promise<{ data: ProjectAssessmentAverageDropsDTO[] }> => {
  return axiosBase.get('/api/project/assessment/average');
};

export const fetchHRMProjects = (): Promise<{ data: HRMProjectDTO[] }> => {
  return axiosBaseForExternalApp.get(`${HRM_LINK}/api/projects/ReturnListProjectClient`);
};

export const fetchAllActivitiesByProjectId = async (id: number): Promise<{ data: HRMActivityDTO[] }> => {
  const response = await axiosBaseForExternalApp.get<HRMActivityDTO[]>(
    `${HRM_LINK}/api/projectactivity/ReturnAllProjectActivity`
  );

  const filteredData = response.data.filter(activity => activity.projectId === id);
  return { data: filteredData };
};

export async function buildProjectRequestData(project?: ProjectDTO): Promise<ProjectRequestDTO> {
  if (project) {
    const projectFormData = {
      id: project.id,
      name: project.name,
      description: project.description,
      sponsorId: '' + project.sponsor.id,
      managerId: '' + project.manager.id,
      projectType: project.projectType,
      startDate: formatDateToVTextField(project.startDate),
      endDate: formatDateToVTextField(project.endDate),
      activities: [] as string[]
    };
    if (project.hrmProjectId) {
      const { data: hrmActivities } = await fetchAllActivitiesByProjectId(project.hrmProjectId);
      const extraActivities = (hrmActivities || [])
        .filter(activity => project.activities?.includes(activity.name))
        .map(activity => activity.name);
      return {
        ...projectFormData,
        hrmProjectId: project.hrmProjectId,
        clientId: project.clientId,
        activities: extraActivities
      } as ProjectRequestDTO;
    }
    return projectFormData;
  }
  return {
    name: '',
    description: '',
    sponsorId: '',
    managerId: '',
    projectType: '',
    startDate: '',
    endDate: ''
  };
}

export const updateProjectArchiveState = (projectId: number, state: boolean): Promise<void> => {
  return axiosBase.put(`/api/project/${projectId}/archive/${state}`);
};

export const updateProjectManagers = (
  projectId: number,
  assessmentId: number,
  managers: number[]
): Promise<{ status: number }> => {
  return axiosBase.post(`/api/project/${projectId}/assessment/${assessmentId}/updateManagers`, managers);
};

export const fetchProjectManagers = (
  projectId: number,
  assessmentPeriodId: number
): Promise<{ data: ProjectManager[] }> => {
  return axiosBase.get(`/api/manager/project/${projectId}/assessment/${assessmentPeriodId}`);
};

export const updateManagerStatus = ({
  projectId,
  assessmentPeriodId,
  managerId,
  status
}: {
  projectId: number;
  assessmentPeriodId: number;
  managerId: number;
  status: typeof MANAGER_STATUS;
}): Promise<{ data: string }> => {
  return axiosBase.post(`/api/manager/updateStatus/project/${projectId}/assessment/${assessmentPeriodId}`, {
    managerId,
    status
  });
};
